// Tenantra Inc. - Tenant panel Messages: send a templated email to the tenant and see every message sent
import { useCallback, useEffect, useState } from 'react';
import { Mail, Loader2, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import api, { apiHelpers } from '@/services/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Section, fmtDate } from './DetailPanel';
import { Pill } from './ScreeningAddresses';

const STATUS = { sent: ['Sent', 'emerald'], not_delivered: ['Not delivered', 'slate'], failed: ['Failed', 'red'] };
const LABEL = { rent_reminder: 'Rent reminder', payment_bounced: 'Payment bounced', lease_renewal: 'Lease renewal', custom: 'Custom message' };

function ComposeDialog({ open, onClose, data, tenancyId, onSent }) {
  const [key, setKey] = useState('rent_reminder');
  const [f, setF] = useState({ subject: '', body: '' });
  const [busy, setBusy] = useState(false);
  const pick = useCallback((k) => { const t = data.templates.find(x => x.key === k); setKey(k); setF({ subject: t.subject, body: t.body }); }, [data]);
  useEffect(() => { if (open) pick('rent_reminder'); }, [open, pick]);
  const send = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/landlord/tenancies/${tenancyId}/messages`, { template: key, ...f });
      ({ sent: () => toast.success('Message sent'), not_delivered: () => toast.warning('Logged, not delivered to a test address') }[r.data.status] || (() => toast.error(r.data.error || 'Message failed')))();
      onSent(); onClose();
    } catch (e) { toast.error(apiHelpers.handleError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg bg-white" data-testid="message-dialog">
        <DialogHeader>
          <DialogTitle>Message tenant</DialogTitle>
          <DialogDescription>Sent by email to {data.to_email}. Replies go to your email.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex flex-wrap gap-1.5" data-testid="message-templates">
            {data.templates.map(t => (
              <button key={t.key} onClick={() => pick(t.key)} className={`h-7 px-3 rounded-full text-xs transition-colors ${key === t.key ? 'bg-teal-50 text-teal-800 font-medium' : 'bg-slate-100/70 text-slate-500 hover:text-slate-800'}`} data-testid={`message-template-${t.key}`}>{t.label}</button>
            ))}
          </div>
          {data.test_address && <p className="text-xs text-amber-700 bg-amber-50 rounded-md px-3 py-2" data-testid="message-test-address">This tenant has a test email address, so the message will be logged but not delivered.</p>}
          <div><Label htmlFor="msg-subject" className="text-xs">Subject</Label><Input id="msg-subject" value={f.subject} maxLength={150} onChange={(e) => setF(s => ({ ...s, subject: e.target.value }))} className="h-10 mt-1" data-testid="message-subject" /></div>
          <div><Label htmlFor="msg-body" className="text-xs">Message</Label><Textarea id="msg-body" value={f.body} maxLength={5000} rows={11} onChange={(e) => setF(s => ({ ...s, body: e.target.value }))} className="mt-1 text-sm leading-relaxed" data-testid="message-body" /></div>
          <button onClick={send} disabled={busy || f.subject.trim().length < 2 || f.body.trim().length < 2} className="w-full h-10 rounded-md bg-teal-600 hover:bg-teal-700 transition-colors text-white text-sm font-medium disabled:opacity-40 inline-flex items-center justify-center gap-2" data-testid="message-send">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Send email
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function MessageRow({ m }) {
  const [open, setOpen] = useState(false);
  const st = STATUS[m.status] || [m.status, 'slate'];
  return (
    <li data-testid={`message-row-${m.id}`}>
      <button onClick={() => setOpen(o => !o)} className="w-full px-3 py-2.5 flex items-center gap-3 text-left text-sm hover:bg-slate-50/70 transition-colors" data-testid={`message-toggle-${m.id}`}>
        <div className="flex-1 min-w-0">
          <p className="text-slate-800 truncate">{m.subject}</p>
          <p className="text-xs text-slate-500">{fmtDate(m.created_at)} · {LABEL[m.template] || m.template} · {m.auto ? 'Automatic' : m.sent_by_name || 'Team'}</p>
        </div>
        <span className="flex-shrink-0"><Pill tone={st[1]} testId={`message-status-${m.id}`}>{st[0]}</Pill></span>
        <ChevronDown className={`w-3.5 h-3.5 text-slate-400 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <p className="px-3 pb-3 text-xs text-slate-600 whitespace-pre-wrap leading-relaxed" data-testid={`message-body-${m.id}`}>{m.body}</p>}
    </li>
  );
}

export function TenantMessages({ tenancyId }) {
  const [data, setData] = useState(null);
  const [composing, setComposing] = useState(false);
  const load = useCallback(() => api.get(`/landlord/tenancies/${tenancyId}/messages`).then(r => setData(r.data)).catch(() => setData(false)), [tenancyId]);
  useEffect(() => { setData(null); load(); }, [load]);
  if (data === false) return null;
  return (
    <Section title="Messages" aside={data?.to_email && <button onClick={() => setComposing(true)} className="text-xs font-medium text-teal-700 hover:text-teal-800 inline-flex items-center gap-1" data-testid="message-compose"><Mail className="w-3.5 h-3.5" /> Message tenant</button>}>
      {!data ? <div className="h-12 bg-slate-100 rounded animate-pulse" /> : data.messages.length === 0 ? (
        <p className="text-sm text-slate-500" data-testid="messages-empty">{data.to_email ? 'No messages sent yet.' : 'This tenant has no email address.'}</p>
      ) : (
        <ul className="divide-y divide-slate-100 border border-slate-100 rounded-md" data-testid="messages-list">{data.messages.map(m => <MessageRow key={m.id} m={m} />)}</ul>
      )}
      {data && <ComposeDialog open={composing} onClose={() => setComposing(false)} data={data} tenancyId={tenancyId} onSent={load} />}
    </Section>
  );
}
